import { action } from '@ember/object';
import { trackedObject } from '@ember/reactive/collections';
import Service, { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  TRIAL_ACTIONS,
  type TrialAction,
} from 'lumen/utils/constants/short-term-rental-constants';

type ShortTermRentalTrialData = {
  days_remaining: number;
  should_show_end_trial_popup: boolean;
  should_show_midway_trial_popup: boolean;
};

type ShortTermRentalUpsellData = {
  nr_of_properties: number;
  relevant_nr_of_properties: number;
  short_term_rental_trial: ShortTermRentalTrialData;
};

export default class ShortTermRentalUpsellService extends Service {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  // Tracked object to store the state of the Short-Term Rental upsell banner when the user dismisses it
  // Why? When user dismisses a banner, we store this information in localStorage directly.
  // But making changes to the localStorage doesn't trigger reactivity and as a result UI doesn't update accordingly i.e. the banner doesn't disappear.
  // Therefore, we also update this information in a tracked object in order to trigger reactivity.
  bannerVisibilityStates = trackedObject();

  requestDismissTrialTask = task({ drop: true }, async () => {
    try {
      await this.executeTrialAction(TRIAL_ACTIONS.DISMISS);
      await this.store.query<Hotel>('hotel', {
        id: this.hotelAndMarketService.hotel.id,
      });
      this.router.transitionTo('hotel.rates');
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }

    return null;
  });

  requestGetInTouchTrialTask = task({ drop: true }, async () => {
    try {
      await this.executeTrialAction(TRIAL_ACTIONS.GET_IN_TOUCH);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showWarningAlert({
        title: this.intl.t('short_term_rental.trial.get_in_touch.modal.title'),
        text: this.intl.t('short_term_rental.trial.get_in_touch.modal.text'),
        icon: '100x100_exit',
        buttons: {
          confirm: {
            text: this.intl.t('general.got_it'),
          },
        },
      });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.requestUpsellInfoDataTask.perform();
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }

    return null;
  });

  requestNotInterestedTrialTask = task({ drop: true }, async () => {
    try {
      await this.executeTrialAction(TRIAL_ACTIONS.DISMISS);

      const confirmWasClicked = await this.alerting.showSuccessAlert({
        title: this.intl.t(
          'short_term_rental.trial.not_interested.modal.title',
        ),
        text: this.intl.t('short_term_rental.trial.not_interested.modal.text'),
        icon: '100x100_send',
        buttons: {
          confirm: {
            text: this.intl.t('general.got_it'),
          },
        },
      });
      if (confirmWasClicked) {
        // remove Short-Term Rental FF
        await this.store.query<Hotel>('hotel', {
          id: this.hotelAndMarketService.hotel.id,
        });
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.requestUpsellInfoDataTask.perform();
        this.transition.redirectToEnabledTab();
      }
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }

    return null;
  });

  requestNotNowTrialTask = task({ drop: true }, async () => {
    try {
      await this.executeTrialAction(TRIAL_ACTIONS.NOT_NOW);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.requestUpsellInfoDataTask.perform();
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }

    return null;
  });

  requestStartTrialTask = task({ drop: true }, async () => {
    try {
      await this.executeTrialAction(TRIAL_ACTIONS.START);
      await this.store.query<Hotel>('hotel', {
        id: this.hotelAndMarketService.hotel.id,
      });
      this.router.transitionTo('hotel.short-term-rental');
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }

    return null;
  });

  requestUpsellInfoDataTask = task({ restartable: true }, async () => {
    try {
      const requestUrl = formatUrl(
        '/api/v3/shorttermrental/upsell/info/',
        {
          subscription_id: this.hotelAndMarketService.hotel.id!,
        },
        this.ajax.host,
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return await this.ajax.request(requestUrl);
    } catch (error) {
      this.errorTracking.captureException(error);
    }

    return null;
  });

  // The banner can be shown if:
  // - The user has access to the upsell feature
  // - The user hasn't dismissed the banner
  // If the banner is dismissed the user should still be able to access the trial through the Short-Term rental tab
  get canShowShortTermRentalUpsellBanner(): boolean {
    return (
      this.access.hasFeature('short_term_rental.upsell') &&
      Boolean(this.showShortTermRentalUpsellBanner)
    );
  }

  get daysLeftInTrial(): number | undefined {
    return this.shortTermRentalTrialData?.days_remaining;
  }

  get isShortTermRentalEndTrial(): boolean | undefined {
    return this.shortTermRentalTrialData?.should_show_end_trial_popup;
  }

  get isShortTermRentalMidwayTrial(): boolean | undefined {
    return this.shortTermRentalTrialData?.should_show_midway_trial_popup;
  }

  get isShortTermRentalUpsellAvailable(): boolean {
    return this.access.hasFeature('short_term_rental.upsell');
  }

  get localStorageKey(): string {
    return `isShortTermRentalUpsellBannerVisible_${this.hotelAndMarketService.hotel?.id}`;
  }

  // Amount of properties with which the sub is competing i.e. properties available in Short-Term rental
  get shortTermRentalProperties(): number {
    return this.shortTermRentalUpsellData?.nr_of_properties ?? 0;
  }

  // Amount of relevant properties with which the sub is competing i.e. properties available in Short-Term rental
  get shortTermRentalRelevantProperties(): number {
    return this.shortTermRentalUpsellData?.relevant_nr_of_properties ?? 0;
  }

  get shortTermRentalTrialData(): ShortTermRentalTrialData | undefined {
    return this.shortTermRentalUpsellData?.short_term_rental_trial;
  }

  get shortTermRentalUpsellData(): ShortTermRentalUpsellData | undefined {
    if (this.requestUpsellInfoDataTask.last?.value) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.requestUpsellInfoDataTask.last.value;
    }

    return undefined;
  }

  // These banners can be dismissed by the user. This property indicates if that's the case or not.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showShortTermRentalUpsellBanner() {
    // @ts-expect-error: Incorrect type
    // If bannerVisibilityStates has the key, return the value from there
    if (this.bannerVisibilityStates[this.localStorageKey]) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.bannerVisibilityStates[this.localStorageKey];
    }

    // Else, fetch the STR upsell banner state from local storage
    if (this.localStorage.getItem(this.localStorageKey)) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return JSON.parse(this.localStorage.getItem(this.localStorageKey));
    }

    return true;
  }

  async executeTrialAction(trialAction: TrialAction): Promise<void> {
    const requestUrl = formatUrl(
      '/api/v3/shorttermrental/upsell/',
      {
        subscription_id: this.hotelAndMarketService.hotel.id!,
      },
      this.ajax.host,
    );

    await this.ajax.request(requestUrl, {
      method: 'POST',
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
      data: JSON.stringify({
        action: trialAction,
      }),
    });
  }

  // Close the Short-Term rental upsell banner in RI when the user clicks Dismiss
  @action onShortTermRentalUpsellBannerDismiss(): void {
    this.localStorage.setItem(this.localStorageKey, JSON.stringify(false));

    // Update banner visibility state to trigger reactivity i.e. UI update
    // @ts-expect-error: Incorrect type
    this.bannerVisibilityStates[this.localStorageKey] = false;
  }

  @action onShortTermRentalUpsellBannerPrimaryButtonClick(): void {
    this.router.transitionTo('hotel.short-term-rental-upsell');
  }
}

declare module '@ember/service' {
  interface Registry {
    'short-term-rental-upsell': ShortTermRentalUpsellService;
  }
}
